/**
 * Monta linhas da lista ecommerce (1 transação = 1 linha).
 * @module buildDocumentTransactionRows
 */

import type { Patient } from "fhir/r5";
import { ecommercePatientReference } from "./patientEcommerceOpState";
import {
  formatTransactionLabel,
  transactionCreatedAt,
  type DocumentTransactionRow,
} from "./documentTransactions";
import { peekPatientTransactions } from "./patientDocStatusCache";
import { resolveTransactionOpState } from "./transactionEcommerceOpState";
import { peekTransactionPurchaseCompleted } from "./transactionReleaseLookup";

export function getTransactionRowKey(
  patientKey: string,
  transactionId: string,
): string {
  return `${patientKey}::${transactionId}`;
}

export function parseTransactionRowKey(rowKey: string): {
  patientKey: string;
  transactionId: string;
} {
  const separator = rowKey.indexOf("::");
  if (separator < 0) {
    return { patientKey: rowKey, transactionId: "" };
  }
  return {
    patientKey: rowKey.slice(0, separator),
    transactionId: rowKey.slice(separator + 2),
  };
}

export function buildDocumentTransactionRows(
  patients: Patient[],
  organizationId?: string,
): DocumentTransactionRow[] {
  const rows: DocumentTransactionRow[] = [];

  for (const patient of patients) {
    const patientKey = ecommercePatientReference(patient);
    if (!patientKey) continue;

    const transactions = peekPatientTransactions(patientKey);
    if (!transactions.length) continue;

    for (const transaction of transactions) {
      const opState = resolveTransactionOpState(
        transaction,
        patientKey,
        organizationId,
      );
      const createdAt = transactionCreatedAt(transaction);
      rows.push({
        transactionId: transaction.transactionId,
        patient,
        prescriptionDoc: transaction.prescriptionDoc,
        anvisaDoc: transaction.anvisaDoc,
        sharedDocs: transaction.sharedDocs,
        opState,
        purchaseCompleted: peekTransactionPurchaseCompleted(
          patientKey,
          transaction.transactionId,
        ),
        createdAt,
      });
    }
  }

  return rows;
}

export function getTransactionDisplayLabel(row: DocumentTransactionRow): string {
  return formatTransactionLabel({
    transactionId: row.transactionId,
    createdAt: row.createdAt,
  });
}
